/**
 * `@richrich/i18n` — core entry (ZERO runtime dependencies, isomorphic).
 *
 * Framework- and platform-agnostic translation so the same catalog serves the web
 * app today and React Native later. The React binding lives in a separate subpath
 * (`@richrich/i18n/react`) and is the only place React is imported; this module
 * touches no DOM/React API.
 *
 * CONTRACT — `createTranslator(locale).t(key, params?)`:
 *   • `key` is a compile-checked `MessageKey` (`<namespace>.<localKey>`).
 *   • `{name}` placeholders are interpolated from `params` (string | number).
 *   • Resolution never throws: missing/blank localized value → English → the key
 *     itself. An unknown placeholder token is left verbatim.
 */
import { en, type MessageKey } from "./messages/en";
import { ko } from "./messages/ko";

/** The supported locales. Only these two exist. */
export type Locale = "en" | "ko";

/** Default UI language (Korean). No navigator/OS detection is performed here. */
export const DEFAULT_LOCALE: Locale = "ko";

export type { MessageKey };

/** Interpolation values for `{placeholder}` tokens. */
export type TranslateParams = Readonly<Record<string, string | number>>;

/** A bound translator for one locale. */
export interface Translator {
  /** Resolve `key` for this locale with `{name}`-style interpolation. */
  t: (key: MessageKey, params?: TranslateParams) => string;
}

// Index-signature typing (rather than the precise `Record<MessageKey, string>`)
// so a lookup is honestly `string | undefined` — this is what makes the
// three-tier fallback below real code, not a type-erased no-op.
const CATALOGS: Readonly<Record<Locale, Readonly<Record<string, string>>>> = {
  en,
  ko,
};

/** Substitute `{name}` tokens; unknown tokens are left untouched. */
function interpolate(template: string, params: TranslateParams | undefined): string {
  if (params === undefined) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

/**
 * Resolve one key against a `primary` catalog with English `fallback`, degrading
 * primary → English → the key string itself. Pure and side-effect free; exported
 * so non-React consumers (and tests) can resolve without binding a translator. A
 * blank (`''`) primary value is treated as absent so a stubbed entry falls back.
 */
export function resolveMessage(
  key: string,
  primary: Readonly<Record<string, string>>,
  fallback: Readonly<Record<string, string>>,
): string {
  const localized = primary[key];
  if (localized !== undefined && localized !== "") return localized;
  return fallback[key] ?? key;
}

/**
 * Build a translator bound to `locale`. Lookups fall back to English then to the
 * key so a not-yet-translated string degrades to readable text, never a throw.
 */
export function createTranslator(locale: Locale): Translator {
  const primary = CATALOGS[locale];
  return {
    t(key: MessageKey, params?: TranslateParams): string {
      return interpolate(resolveMessage(key, primary, en), params);
    },
  };
}
